import {
  ADMIN_PORTAL_ROLES,
  COLLECTIONS,
  PLATFORM_ORDER_SIGNAL_ID,
  ROLES,
  STAFF_MODULES,
} from '@giftsay/constants';
import { toast } from '@giftsay/ui';
import type { OrderSignal, OrderSignalEntry } from '@giftsay/types';
import { formatCurrency } from '@giftsay/utils';
import { doc, onSnapshot } from 'firebase/firestore';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { firestore } from '../../../lib/firebase.js';
import { useAppSelector } from '../../../store/hooks.js';
import { useStaffAccess } from '../../staff/hooks/use-staff-access.js';
import { useStoreNotifications } from '../../stores/hooks/use-store-notifications.js';

const EMPTY_SIGNAL: OrderSignal = { seq: 0, recent: [], updatedAt: '' };

// Per browser, per signal doc. "Seen" drives the badge; "alerted" makes sure only one of several
// open portal tabs beeps/toasts for the same order.
const seenKey = (signalId: string) => `giftsay:orderSignal:${signalId}:seen`;
const alertedKey = (signalId: string) => `giftsay:orderSignal:${signalId}:alerted`;

function readNumber(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? null : Number(raw);
  } catch {
    return null;
  }
}

function writeNumber(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Storage blocked (private mode etc.) — the badge just won't survive a reload.
  }
}

let audioContext: AudioContext | null = null;

/** A short two-tone chime, synthesized so there's no sound file to ship. Browsers only allow it
 *  once the user has interacted with the page — before that it silently does nothing. */
function playChime(): void {
  try {
    audioContext ??= new AudioContext();
    const ctx = audioContext;
    void ctx.resume();
    [880, 1320].forEach((frequency, index) => {
      const start = ctx.currentTime + index * 0.18;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.3);
      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.32);
    });
  } catch {
    // No Web Audio — the toast still shows.
  }
}

export function describeOrderSignal(entry: OrderSignalEntry): string {
  return `${formatCurrency(entry.total)} · ${entry.itemCount} item${entry.itemCount === 1 ? '' : 's'}`;
}

/** The `orderSignals` doc this user should watch, or null when they shouldn't hear about orders. */
function useSignalId(): string | null {
  const user = useAppSelector((state) => state.auth.user);
  const { canView } = useStaffAccess();
  const { orderInApp } = useStoreNotifications();
  if (!user || !canView(STAFF_MODULES.ORDERS)) return null;
  // An admin can switch a store's bell off; platform staff always keep their own.
  if (user.role === ROLES.VENDOR) return orderInApp ? user.storeId : null;
  return ADMIN_PORTAL_ROLES.includes(user.role) ? PLATFORM_ORDER_SIGNAL_ID : null;
}

/**
 * Live new-order feed for the portal header. One Firestore listener on a single tiny doc — a
 * read when the portal opens and one per new order, no polling and no API calls. The unread
 * count is `seq` minus the last `seq` this browser marked as seen.
 */
export function useOrderSignals() {
  const signalId = useSignalId();
  const navigate = useNavigate();
  const [signal, setSignal] = React.useState<OrderSignal>(EMPTY_SIGNAL);
  const [lastSeen, setLastSeen] = React.useState(0);

  React.useEffect(() => {
    if (!signalId) {
      setSignal(EMPTY_SIGNAL);
      return;
    }

    let previousSeq: number | null = null;
    const unsubscribe = onSnapshot(
      doc(firestore, COLLECTIONS.ORDER_SIGNALS, signalId),
      (snapshot) => {
        const next = (snapshot.data() as OrderSignal | undefined) ?? EMPTY_SIGNAL;
        setSignal(next);

        if (previousSeq === null) {
          // First load in this browser: start from "all caught up" rather than flagging every
          // order the store has ever had.
          const stored = readNumber(seenKey(signalId));
          if (stored === null) writeNumber(seenKey(signalId), next.seq);
          setLastSeen(stored ?? next.seq);
          writeNumber(
            alertedKey(signalId),
            Math.max(readNumber(alertedKey(signalId)) ?? 0, next.seq),
          );
        } else if (next.seq > previousSeq) {
          const alreadyAlerted = readNumber(alertedKey(signalId)) ?? 0;
          if (next.seq > alreadyAlerted) {
            writeNumber(alertedKey(signalId), next.seq);
            const fresh = next.recent.slice(0, next.seq - Math.max(previousSeq, alreadyAlerted));
            alertNewOrders(fresh, (orderId) => navigate(`/orders/${orderId}`));
          }
        }
        previousSeq = next.seq;
      },
      (error) => {
        console.warn('[order-signal] listener stopped:', error);
      },
    );
    return unsubscribe;
  }, [signalId, navigate]);

  // Keep the badge in step when another tab marks orders as seen.
  React.useEffect(() => {
    if (!signalId) return;
    const onStorage = (event: StorageEvent) => {
      if (event.key === seenKey(signalId) && event.newValue !== null) {
        setLastSeen(Number(event.newValue));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [signalId]);

  const markSeen = React.useCallback(() => {
    if (!signalId) return;
    writeNumber(seenKey(signalId), signal.seq);
    setLastSeen(signal.seq);
  }, [signalId, signal.seq]);

  return {
    enabled: signalId !== null,
    recent: signal.recent,
    unread: Math.max(0, signal.seq - lastSeen),
    markSeen,
  };
}

function alertNewOrders(entries: OrderSignalEntry[], open: (orderId: string) => void): void {
  if (entries.length === 0) return;
  playChime();

  const [latest] = entries;
  const title =
    entries.length === 1 ? `New order ${latest!.orderNumber}` : `${entries.length} new orders`;
  const description = entries.length === 1 ? describeOrderSignal(latest!) : undefined;

  toast.success(title, {
    description,
    duration: 10_000,
    action: { label: 'View', onClick: () => open(latest!.orderId) },
  });

  // A desktop notification too when the portal is in a background tab — that's when the toast
  // and chime are most likely to be missed.
  if (
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted' &&
    document.visibilityState === 'hidden'
  ) {
    const notification = new Notification(title, {
      body: description ?? entries.map((entry) => entry.orderNumber).join(', '),
      tag: `order-${latest!.orderId}`,
    });
    notification.onclick = () => {
      window.focus();
      open(latest!.orderId);
      notification.close();
    };
  }
}
